import type { SupportTicketListEntry } from "../supportTicketTypes";
import {
  SUPPORT_TICKET_STATUS_LABELS,
  SUPPORT_TICKET_STATUS_TAG_COLORS,
} from "../utils/supportTicketStatusDisplay";

/**
 * Props for {@link SupportTicketListItem}.
 */
interface SupportTicketListItemProps {
  /** Ticket summary shown in the sidebar row. */
  ticket: SupportTicketListEntry;
  /** Whether this row is the active selection. */
  isSelected: boolean;
  /** Called when the user selects this ticket row. */
  onSelect: (ticketId: string) => void;
}

/**
 * Single selectable row in the support tickets sidebar list.
 *
 * @param props - Ticket data, selection state, and select handler.
 * @returns Styled support ticket list row.
 */
const SupportTicketListItem = ({
  ticket,
  isSelected,
  onSelect,
}: SupportTicketListItemProps) => {
  const statusLabel = SUPPORT_TICKET_STATUS_LABELS[ticket.status];
  const statusColors = SUPPORT_TICKET_STATUS_TAG_COLORS[ticket.status];

  return (
    <div
      className={`support-tickets-list-item w-full border-b border-primaryGray-14 bg-primaryGray-16 px-4 py-3 text-left transition-colors last:border-b-0 hover:bg-primaryGray-15 ${
        isSelected
          ? "border-l-4 border-l-tertiaryBlue-5 bg-tertiaryBlue-13"
          : "bg-white"
      }`}
      onClick={() => onSelect(ticket.id)}
      aria-pressed={isSelected}
    >
      <div className="truncate text-sm font-semibold leading-[1.125rem] text-primaryGray-1">
        {ticket.title}
      </div>
      <div className="mt-1.5 flex items-center justify-between gap-2">
        <span className="min-w-0 truncate text-xs font-normal leading-4 text-primaryGray-7">
          {ticket.timestamp}
        </span>
        <span
          className="inline-flex shrink-0 rounded px-2.5 py-0.5 text-xs font-semibold"
          style={{
            backgroundColor: statusColors.backgroundColor,
            color: statusColors.textColor,
          }}
          aria-label={`Status: ${statusLabel}`}
          title={statusLabel}
        >
          {statusLabel}
        </span>
      </div>
    </div>
  );
};

export default SupportTicketListItem;
